/* eslint-disable @typescript-eslint/no-explicit-any */
import { Vocabulary } from "../Utils/Vocabulary";
import { urlEnum } from "../Utils/UrlEnum";
import MainTable from "../Components/GenericComponents/MainTable";
import { useContext, useEffect, useState } from "react";
import { ModelModel, BrandModel } from "../Utils/Models";
import { updateData } from "../Services/updateData";
import { postData } from "../Services/postData";
import Model from "../Components/Models/Model";
import { CacheWebsocketContext } from "../Contexts/WebsocketContext";
import { Autocomplete, Grid, IconButton, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import { getData } from "../Services/getData";
import { toast } from "react-toastify";
import GenericFilters from "../Components/GenericComponents/GenericFilters";

export default function DeviceModels() {
  const websocketContext = useContext(CacheWebsocketContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<ModelModel | null>(null);
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const [brands, setBrands] = useState<Array<BrandModel>>([]);
  const [filters, setFilters] = useState<any>({
    brandId: null,
  });

  /**
   * Ensure brandId is always in URL
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("brandId")) {
      params.set("brandId", "null");
      navigate({
        pathname: location.pathname,
        search: params.toString(),
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Parse URL parameters for filters
   */
  useEffect(() => {
    const search = location.search.split(/[?&]+/);
    const newFilters: any = Object.assign({}, filters);
    search.forEach((value: any) => {
      if (value !== "") {
        const values = value.split("=");
        newFilters[values[0]] = isNaN(+values[1]) ? values[1] : +values[1];
        if (values[0] === "page") newFilters[values[0]] = +values[1] - 1;
        if (values[1] === "null") newFilters[values[0]] = null;
      }
    });
    setFilters(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   * Fetch brands for filter
   */
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const data = await getData(urlEnum.brands);
        setBrands(data.data.result);
      } catch (error) {
        toast.error(`${error}`);
      }
    };
    fetchBrands();
  }, []);

  const modelHeader = [
    {
      label: Vocabulary.name,
      name: "name",
      options: { sort: false },
    },
    {
      label: Vocabulary.brand,
      name: "brand",
      options: {
        sort: false,
        customBodyRender: (value: any) => {
          return <>{value && value.name ? value.name : null}</>;
        },
      },
    },
  ];

  /**
   *
   * @param item
   */
  function handleEdit(item: ModelModel | null) {
    setShouldUpdate(false);
    setOpen(!open);
    setEditItem(item);
  }

  /**
   *
   * @param model
   */
  function addOrUpdateModel(model: ModelModel | null) {
    if (model?._id) {
      updateData(`${urlEnum.deviceModels}/${model._id}`, model)
        .then((res) => {
          if (res) {
            websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
            setOpen(false);
            setShouldUpdate(true);
          }
        })
        .catch((err) => {
          setOpen(false);
        });
    } else {
      postData(urlEnum.deviceModels, model)
        .then((res: any) => {
          if (res) {
            websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
            setOpen(false);
            setShouldUpdate(true);
          }
        })
        .catch((err) => {
          setOpen(false);
        });
    }
  }

  // Don't render until brandId is in URL
  const params = new URLSearchParams(location.search);
  if (!params.has("brandId")) {
    return null;
  }

  // Construct API URL with brandId if present
  const apiUrl = filters.brandId && filters.brandId !== "null"
    ? `${urlEnum.deviceModels}/${filters.brandId}`
    : urlEnum.deviceModels;

  return (
    <div>
      <GenericFilters>
        <Grid container spacing={2} style={{ marginBottom: 10 }} alignItems="center">
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={brands}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              value={
                brands.filter(
                  (brand: BrandModel) => brand._id === filters.brandId
                )[0] || null
              }
              freeSolo={false}
              selectOnFocus
              handleHomeEndKeys
              renderInput={(params) => (
                <TextField {...params} label={Vocabulary.brand} size="small" />
              )}
              onChange={(event, newValue: any) => {
                const params = new URLSearchParams(location.search);
                params.set("brandId", newValue ? newValue._id : null);
                navigate({
                  pathname: location.pathname,
                  search: params.toString(),
                });
              }}
            />
          </Grid>
          <Grid item>
            <IconButton
              color="error"
              onClick={() => {
                const params = new URLSearchParams(location.search);
                params.set("brandId", "null");
                navigate({
                  pathname: location.pathname,
                  search: params.toString(),
                });
              }}
              disabled={!filters.brandId || filters.brandId === "null"}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      </GenericFilters>
      <MainTable
        urlEnumApi={apiUrl}
        key={filters.brandId}
        header={modelHeader}
        tableTitle={Vocabulary.modelsList}
        deleteMessages={{
          title: Vocabulary.deleteModel,
          text: Vocabulary.deleteConfirmationModel,
        }}
        hideOptionButtons={{
          edit: {
            condition: (item) => false,
          },
          delete: {
            condition: (item) => false,
          },
        }}
        shouldUpdate={shouldUpdate}
        handleEdit={(item) => {
          handleEdit(item);
        }}
      />
      {open ? (
        <Model
          editModel={editItem}
          open={open}
          addOrUpdateModel={(model) => addOrUpdateModel(model)}
          onClose={() => handleEdit(null)}
        />
      ) : null}
    </div>
  );
}
